import { useState, useEffect, useRef, useCallback } from 'react'
import { pushTemplatesApi, usersApi } from '../services/api'
import {
  findMissingRequiredVariables,
  formatMissingVariablesError,
  getInputType,
  normalizeVariable,
} from '../utils/templateVariableHelpers'

const SendPushTestModal = ({ template, templateTypeName, onCancel }) => {
  const [userId, setUserId] = useState('')
  const [templateData, setTemplateData] = useState({})
  const [variables, setVariables] = useState([])
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  // User search state
  const [userSearch, setUserSearch] = useState('')
  const [userResults, setUserResults] = useState([])
  const [userSearching, setUserSearching] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [showDropdown, setShowDropdown] = useState(false)
  const searchTimeoutRef = useRef(null)
  const dropdownRef = useRef(null)

  useEffect(() => {
    loadVariables()
  }, [template]) // eslint-disable-line react-hooks/exhaustive-deps

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const loadVariables = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await pushTemplatesApi.getVariablesByType(template.templateType)

      let variableList = []
      if (response.data && Array.isArray(response.data.variables)) {
        variableList = response.data.variables
      } else if (Array.isArray(response.data)) {
        variableList = response.data
      } else if (response.data && typeof response.data === 'object') {
        variableList = Object.values(response.data).flat()
      }

      setVariables(variableList)

      const initialData = {}
      variableList.forEach(variable => {
        const variableName = typeof variable === 'string' ? variable : variable.name
        initialData[variableName] = ''
      })
      setTemplateData(initialData)
    } catch (err) {
      setError('Failed to load template variables')
      setVariables([])
    } finally {
      setLoading(false)
    }
  }

  const searchUsers = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setUserResults([])
      setShowDropdown(false)
      return
    }

    try {
      setUserSearching(true)
      const response = await usersApi.list({ searchString: query, pageSize: 10, pageNumber: 1 })
      const items = response.data?.items || response.data?.data || []
      setUserResults(items)
      setShowDropdown(items.length > 0)
    } catch {
      setUserResults([])
    } finally {
      setUserSearching(false)
    }
  }, [])

  const handleSearchChange = (value) => {
    setUserSearch(value)
    setSelectedUser(null)
    setUserId('')

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
    searchTimeoutRef.current = setTimeout(() => searchUsers(value), 300)
  }

  const handleSelectUser = (user) => {
    const id = user.id || user.userId
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ')
    setSelectedUser({ id, name, email: user.email })
    setUserId(id)
    setUserSearch(name ? `${name} (${user.email || id})` : (user.email || id))
    setShowDropdown(false)
  }

  const handleVariableChange = (variableName, value) => {
    setTemplateData(prev => ({
      ...prev,
      [variableName]: value
    }))
  }

  const handleSendTest = async () => {
    if (!userId.trim()) {
      setError('Please select a user or enter a user ID')
      return
    }

    const missingVariables = findMissingRequiredVariables(variables, templateData)
    if (missingVariables.length > 0) {
      setError(formatMissingVariablesError(missingVariables))
      return
    }

    try {
      setSending(true)
      setError(null)

      const request = {
        templateType: template.templateType,
        userId: userId.trim(),
        templateData: templateData
      }

      await pushTemplatesApi.send(request)

      setSuccess(true)
      setTimeout(() => {
        onCancel()
      }, 2000)
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to send test push notification')
    } finally {
      setSending(false)
    }
  }

  const displayName = selectedUser
    ? `${selectedUser.name || selectedUser.email}`
    : userId

  if (success) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
          <div className="text-center">
            <div className="text-green-500 text-4xl mb-4">&#10003;</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Test Push Sent!</h3>
            <p className="text-gray-600">The test push notification has been sent to {displayName}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            Send Test Push - {templateTypeName}
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            &times;
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="text-gray-500">Loading template variables...</div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* User Search */}
            <div ref={dropdownRef} className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Recipient User
              </label>
              <input
                type="text"
                value={userSearch}
                onChange={(e) => handleSearchChange(e.target.value)}
                onFocus={() => { if (userResults.length > 0) setShowDropdown(true) }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Search by name or email..."
              />
              {userSearching && (
                <div className="absolute right-3 top-9 text-xs text-gray-400">Searching...</div>
              )}
              <p className="text-xs text-gray-500 mt-1">
                The user must have a registered device to receive push notifications
              </p>

              {/* Dropdown results */}
              {showDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {userResults.map((user) => {
                    const id = user.id || user.userId
                    const name = [user.firstName, user.lastName].filter(Boolean).join(' ')
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => handleSelectUser(user)}
                        className="w-full text-left px-4 py-2.5 hover:bg-gray-50 border-b border-gray-50 last:border-0 transition-colors"
                      >
                        <div className="text-sm font-medium text-gray-900">{name || 'No name'}</div>
                        <div className="text-xs text-gray-500">{user.email || id}</div>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Selected user info */}
            {selectedUser && (
              <div className="flex items-center gap-2 px-3 py-2 bg-primary-50 border border-primary-200 rounded-md">
                <span className="text-sm text-primary-800 font-medium">{selectedUser.name || selectedUser.email}</span>
                <span className="text-xs text-primary-600">({selectedUser.id})</span>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedUser(null)
                    setUserId('')
                    setUserSearch('')
                  }}
                  className="ml-auto text-primary-500 hover:text-primary-700 text-sm"
                >
                  &times;
                </button>
              </div>
            )}

            {/* Manual ID entry fallback */}
            {!selectedUser && (
              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  Or enter user ID directly
                </label>
                <input
                  type="text"
                  value={userId}
                  onChange={(e) => {
                    setUserId(e.target.value)
                    setUserSearch('')
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="e.g., e2dafee8-e6a5-482d-85f4-048ab94f071b"
                />
              </div>
            )}

            {/* Template Variables */}
            {variables.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Template Variables
                </label>
                <div className="space-y-3">
                  {variables.map((variable, index) => {
                    const { name: variableName, description: variableDescription, isRequired, type: variableType } = normalizeVariable(variable)
                    const inputType = getInputType(variableType)

                    return (
                      <div key={`${variableName}-${index}`}>
                        <label className="block text-xs text-gray-600 mb-1">
                          {variableName} {isRequired && <span className="text-red-500">*</span>}
                          {variableDescription && (
                            <span className="text-gray-500 font-normal ml-1">
                              - {variableDescription}
                            </span>
                          )}
                        </label>
                        <input
                          type={inputType}
                          value={templateData[variableName] || ''}
                          onChange={(e) => handleVariableChange(variableName, e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder={`Enter value for ${variableName}`}
                          required={isRequired}
                        />
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {variables.length === 0 && !loading && (
              <div className="text-gray-500 text-sm">
                No variables required for this template type.
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                onClick={onCancel}
                disabled={sending}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSendTest}
                disabled={sending || !userId.trim()}
                className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sending ? 'Sending...' : 'Send Test Push'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default SendPushTestModal
